// The public status link of a Real Estate deal (ticket 206): show, copy, email, rotate and
// turn off. Shared by the hub's transaction card and the deal page. `onChange` runs after
// a rotate or turn-off so the caller can refresh the deal.
import React, { useState } from 'react';
import { Copy, Mail, Ban, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import { copyText } from '../clipboard';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from '../errors';
import { whyNotSent } from '../shared/whyNotSent';

export default function TransactionShareLink({ tx, onChange }) {
  const shareUrl = `${window.location.origin}/tx/${tx.share_token}`;
  const revoked = !!tx.share_token_revoked_at;
  const [emailTo, setEmailTo] = useState('');
  const [sending, setSending] = useState(false);
  const base = `/re/transactions/${tx.transaction_id}`;
  const copyShare = async () => {
    if (await copyText(shareUrl)) toast({ title: 'Public status link copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };
  const rotate = async () => {
    if (!(await confirmDialog({ title: 'Rotate the status link?', description: 'The current link stops working now. Anyone who has it loses access until you share the new one.', confirmText: 'Rotate', destructive: true }))) return;
    try {
      await api.post(`${base}/regenerate-share-token`); onChange();
      toast({ title: 'Status link rotated' });
    } catch (e) { toast({ title: 'Rotate failed', description: errorMessage(e) }); }
  };
  const revoke = async () => {
    if (!(await confirmDialog({ title: 'Turn off the status link?', description: 'The link stops working now. The transaction is not affected. Rotate it to turn sharing back on.', confirmText: 'Turn off', destructive: true }))) return;
    try {
      await api.post(`${base}/revoke-share-token`); onChange();
      toast({ title: 'Status link turned off' });
    } catch (e) { toast({ title: 'Turn off failed', description: errorMessage(e) }); }
  };
  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to) return;
    // A real email to someone outside the company: one stray click must not send it.
    if (!(await confirmDialog({ title: 'Email the status link?', description: `This sends an email to ${to}.`, confirmText: 'Send email' }))) return;
    setSending(true);
    try {
      const { data: r } = await api.post(`${base}/share-link/send`, { to });
      if (r.email_sent) { toast({ title: 'Link emailed', description: `Sent to ${r.sent_to}` }); setEmailTo(''); }
      else toast({ title: 'Link not sent', description: `${whyNotSent(r.reason)} Use Copy instead.` });
    } catch (e) {
      toast({ title: 'Link not sent', description: errorMessage(e) });
    } finally { setSending(false); }
  };
  return (
    <div data-testid={`share-panel-${tx.transaction_id}`}>
      {revoked && (
        <p className="text-[11px] text-red-600 mb-2" data-testid={`share-revoked-${tx.transaction_id}`}>This link is turned off. Rotate it to make a new link and turn sharing back on.</p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className={`flex-1 min-w-0 font-mono text-[11px] bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 truncate ${revoked ? 'line-through text-slate-400' : ''}`}>{shareUrl}</div>
        <button onClick={copyShare} disabled={revoked} data-testid={`copy-tx-link-${tx.transaction_id}`} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"><Copy size={11} /> Copy</button>
        <button onClick={rotate} data-testid={`rotate-tx-link-${tx.transaction_id}`} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><RefreshCw size={11} /> Rotate</button>
        {!revoked && (
          <button onClick={revoke} data-testid={`revoke-tx-link-${tx.transaction_id}`} className="text-xs px-2 py-1.5 rounded border border-slate-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"><Ban size={11} /> Turn off</button>
        )}
      </div>
      {!revoked && (
        <div className="flex items-center gap-2 flex-wrap mt-2">
          <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="Email the link to…" data-testid={`email-tx-input-${tx.transaction_id}`}
            className="flex-1 min-w-0 w-auto text-xs md:text-xs" />
          <button onClick={sendEmail} disabled={sending || !emailTo.trim()} data-testid={`email-tx-btn-${tx.transaction_id}`} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"><Mail size={11} /> Email</button>
        </div>
      )}
    </div>
  );
}
